/** PayrollCoverageMap — celebratory 50-state payroll coverage choropleth.
 *  All live states glow emerald; hover shows the rule version, wage base and
 *  income-tax coverage. Data from the PUBLIC /payroll/coverage endpoint. */
import { useEffect, useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import api from "@/lib/api";

const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const FIPS_TO_STATE = {
  "01":"AL","02":"AK","04":"AZ","05":"AR","06":"CA","08":"CO","09":"CT","10":"DE",
  "11":"DC","12":"FL","13":"GA","15":"HI","16":"ID","17":"IL","18":"IN","19":"IA",
  "20":"KS","21":"KY","22":"LA","23":"ME","24":"MD","25":"MA","26":"MI","27":"MN",
  "28":"MS","29":"MO","30":"MT","31":"NE","32":"NV","33":"NH","34":"NJ","35":"NM",
  "36":"NY","37":"NC","38":"ND","39":"OH","40":"OK","41":"OR","42":"PA","44":"RI",
  "45":"SC","46":"SD","47":"TN","48":"TX","49":"UT","50":"VT","51":"VA","53":"WA",
  "54":"WV","55":"WI","56":"WY","72":"PR",
};

const wb = (n) => (n >= 1000 ? `$${(n / 1000).toFixed(n % 1000 ? 1 : 0)}k` : `$${n}`);

export default function PayrollCoverageMap({ compact = false }) {
  const [data, setData] = useState(null);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payroll/coverage");
        setData(data);
      } catch { /* ignore */ }
    })();
  }, []);

  const byState = new Map((data?.states || []).map((s) => [s.code, s]));
  const complete = data && data.live_count >= data.total;

  return (
    <div className="relative" data-testid="payroll-coverage-map">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="inline-flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest rounded-full px-3 py-1 border ${
            complete ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                     : "bg-sky-500/10 text-sky-300 border-sky-500/30"}`}
                data-testid="coverage-count-pill">
            {complete && <span aria-hidden>🎉</span>}
            {data ? `${data.live_count}/${data.total} states payroll-ready` : "Loading coverage…"}
          </span>
          {complete && !compact && (
            <span className="text-[10px] text-slate-400">Every US state — verified 2026 withholding + SUTA rules</span>
          )}
        </div>
        <div className="flex items-center gap-3 text-[10px] text-slate-400">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-400 inline-block" /> Income tax + SUTA</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-teal-600 inline-block" /> No wage tax (SUTA only)</span>
        </div>
      </div>
      <div className={compact ? "max-w-2xl" : ""}>
        <ComposableMap projection="geoAlbersUsa" style={{ width: "100%", height: "auto" }}>
          <Geographies geography={GEO_URL}>
            {({ geographies }) => geographies.map((geo) => {
              const code = FIPS_TO_STATE[geo.id];
              const s = code ? byState.get(code) : null;
              return (
                <Geography key={geo.rsmKey} geography={geo}
                  onMouseEnter={() => s && setHover(s)}
                  onMouseLeave={() => setHover(null)}
                  style={{
                    default: { fill: s ? (s.income_tax ? "#34d399" : "#0d9488") : "#1f2937",
                               stroke: "#0f172a", strokeWidth: 0.6, outline: "none" },
                    hover: { fill: "#fbbf24", stroke: "#0f172a", strokeWidth: 0.8, outline: "none", cursor: "pointer" },
                    pressed: { fill: "#f59e0b", outline: "none" },
                  }} />
              );
            })}
          </Geographies>
        </ComposableMap>
      </div>
      {hover && (
        <div className="absolute bottom-2 left-2 rounded-lg border border-white/10 bg-slate-900/95 backdrop-blur px-3 py-2 text-[11px] shadow-xl pointer-events-none"
             data-testid="coverage-tooltip">
          <div className="font-semibold text-emerald-300">{hover.code} · {hover.version}</div>
          <div className="text-slate-300">{hover.income_tax ? "State income tax withholding + SUTA" : "No wage income tax — SUTA only"}</div>
          <div className="text-slate-400">SUTA wage base {wb(hover.wage_base)}</div>
          {!compact && <div className="text-slate-500 max-w-[260px]">{hover.agency}</div>}
        </div>
      )}
    </div>
  );
}
